"use client";

import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Plus, Minus, FileText, ArrowRight, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqItems = [{
  id: "how-much-is-a-bull",
  question: "How much is a mechanical bull?",
  answer: "There is no single price that applies to every mechanical bull. Current cost depends on the machine, condition, included equipment, accessories and delivery requirements. Compare current inventory for the most relevant pricing."
}, {
  id: "cost-to-buy",
  question: "How much does a mechanical bull cost to buy?",
  answer: "The total purchase cost depends on whether the machine is new or used, the equipment included with it and the delivery arrangement. Check the complete package rather than relying only on the advertised machine price."
}, {
  id: "used-bull-cost",
  question: "How much does a used mechanical bull cost?",
  answer: "Used prices vary according to the exact machine, age, condition, controller, inflatable, known repairs, included equipment and parts availability."
}, {
  id: "new-vs-used-cost",
  question: "Are new mechanical bulls more expensive than used ones?",
  answer: "New equipment often has a higher initial purchase price than comparable used equipment, but the actual difference depends on the individual machines and what each package includes."
}, {
  id: "price-inclusions",
  question: "What is included in the price of a mechanical bull?",
  answer: "It depends on the listing. A package may include the bull, motion base, controller, inflatable safety arena, blower and accessories, while another listing may include fewer components."
}, {
  id: "electric-bull-cost",
  question: "How much does an electric bull cost?",
  answer: "Electric bull pricing depends on the exact machine, controller, included safety equipment, accessories and product-specific configuration. Compare current products instead of applying one generic price to every electric bull."
}, {
  id: "shipping-included",
  question: "Does shipping come with the mechanical bull price?",
  answer: "Shipping terms vary by product and destination. Check the individual listing or confirm the delivery arrangement before purchasing."
}, {
  id: "why-prices-vary",
  question: "Why do mechanical bull prices vary so much?",
  answer: "Prices vary because machines differ in condition, included equipment, controllers, safety arenas, accessories, parts availability and delivery requirements. Used-equipment history can create additional differences."
}];
export default function PricingFaq() {
  const [openId, setOpenId] = useState<string | null>("how-much-is-a-bull");
  const shouldReduceMotion = useReducedMotion();
  const toggleItem = (id: string) => {
    setOpenId(prev => prev === id ? null : id);
  };
  return <section id="pricing-faq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-5 sm:px-8 lg:px-16 max-w-7xl">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-12 md:mb-16 border-b border-border pb-8">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="font-mono text-xs uppercase tracking-widest bg-muted px-2.5 py-1 text-foreground border border-border">
              PRICING GUIDE // SECTION 04
            </span>
            <span className="text-muted-foreground text-sm font-sans flex items-center gap-1.5">
              <HelpCircle className="size-3.5 text-primary" />
              Commercial procurement & pricing guide
            </span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <h2 className="font-serif uppercase font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none text-foreground">
                Mechanical Bull Price FAQ
              </h2>
              <p className="font-sans text-muted-foreground text-base sm:text-lg mt-3 leading-relaxed">
                Essential answers on purchase costs, equipment inclusions, and pricing factors.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Button asChild variant="outline" className="border-border hover:bg-muted">
                <Link to="/ContactPage">
                  Request Custom Quote
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>

        {/* FAQ List Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Main Accordion Column */}
          <div className="lg:col-span-8 border-t border-border">
            {faqItems.map((item, index) => {
            const isOpen = openId === item.id;
            return <div key={item.id} data-index={index} className="border-b border-border transition-colors">
                  <Button variant="ghost" type="button" onClick={() => toggleItem(item.id)} aria-expanded={isOpen} className="w-full h-auto py-5 px-0 text-left flex items-center justify-between gap-4 group cursor-pointer hover:bg-transparent focus-visible:outline-2 focus-visible:outline-ring font-normal">
                    <span className="font-serif uppercase text-lg sm:text-xl font-semibold tracking-wide text-foreground group-hover:text-primary transition-colors">
                      {item.question}
                    </span>
                    <span className="shrink-0 size-8 flex items-center justify-center border border-border bg-muted group-hover:border-foreground text-foreground transition-colors">
                      {isOpen ? <Minus className="size-4 text-primary" /> : <Plus className="size-4" />}
                    </span>
                  </Button>

                  <AnimatePresence initial={false}>
                    {isOpen && <motion.div key="content" initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: "auto",
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: shouldReduceMotion ? 0 : 0.22,
                  ease: [0.2, 0, 0, 1]
                }} className="overflow-hidden">
                        <div className="pb-6 pt-1 pr-4 font-sans text-sm sm:text-base text-muted-foreground leading-relaxed">
                          <p>{item.answer}</p>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>

          {/* Sidebar Buyer Resources Box */}
          <div className="lg:col-span-4">
            <div className="border border-border bg-card p-6 md:p-7 relative">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
                <FileText className="size-5 text-primary shrink-0" />
                <h3 className="font-serif uppercase font-bold text-lg text-foreground tracking-wide">
                  Buyer Guidance & Policies
                </h3>
              </div>

              <div className="space-y-4 text-sm font-sans text-muted-foreground">
                <div className="pb-3 border-b border-border/60">
                  <div className="font-mono text-xs uppercase text-foreground font-semibold">
                    Equipment Packaging
                  </div>
                  <p className="mt-1">
                    Verify included components—such as motion base, controller console, blower, and safety arena—directly on each listing.
                  </p>
                </div>

                <div className="pb-3 border-b border-border/60">
                  <div className="font-mono text-xs uppercase text-foreground font-semibold">
                    Freight & Delivery
                  </div>
                  <p className="mt-1">
                    Shipping logistics and destination requirements vary by location and equipment profile.
                  </p>
                </div>

                <div>
                  <div className="font-mono text-xs uppercase text-foreground font-semibold">
                    Condition Assessment
                  </div>
                  <p className="mt-1">
                    Compare operating history, maintenance records, and available warranty coverage before purchasing.
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-border flex flex-col gap-3">
                <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/ShippingWarrantyAndOrderingPage">
                    View Shipping Policies
                    <ArrowRight className="size-4 ml-1.5" />
                  </Link>
                </Button>

                <Button asChild variant="outline" className="w-full border-border hover:bg-muted">
                  <Link to="/MechanicalBullBuyingGuidePage">
                    <FileText className="size-4 mr-1.5 text-muted-foreground" />
                    Read Full Buying Guide
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}